/**
 * The END-CUSTOMER quote page (phase 3) — the page the Builder's preview was
 * always a rehearsal for. Decision 69 locked the presentation; this component
 * deliberately mirrors `Builder.tsx`'s Preview markup and classes so the two
 * surfaces cannot drift.
 *
 * Reached at /q/<token> (public, no login — main.tsx branches before the
 * portal app mounts, so no portal context, no session, no admin chrome).
 *
 * Modes by payload:
 *   kind 'preview'        -> watermark, no actions, nothing persists (75)
 *   status sent/viewed    -> interactive: sliders, qty bounds, accept/decline/question
 *   status accepted       -> read-only frozen acceptance + "what happens next" (24/74)
 *   status declined       -> read-only notice
 *   status expired        -> read-only notice, question still open (74)
 *   status superseded     -> notice + fresh link onto the successor (73)
 *
 * `?print=1` (decision 70): interaction chrome hidden, `window.__quoteReady`
 * flips when the payload has rendered — the PDF function prints THIS page.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatGBP, formatGBPCompact, round2 } from '@shared/money'
import { pillStyleFor } from '../lib/pills.js'
import { formatUKDate } from '@shared/dates'
import { annualised, computeTotals, includedSectionIds, publicTotals, QTY_MAX, sectionSubtotals, termLabel } from '@shared/pricing'
import {
  amendAuthorityStatement, authorityStatement, DECLINE_REASONS, publicLineToQuoteLine,
  type DeclineReasonKey, type PublicLine,
} from '@shared/customer'
import { QuoteMarkup } from '../lib/markup.js'
import type { SelectionMap } from '@shared/types'

declare global {
  interface Window { __quoteReady?: boolean }
}

// Shape served by GET /api/q/:token (see _lib/quotePublic.ts buildPublicPayload).
interface Payload {
  /** Decision 218: 'amend' = a single-use link that reopens an accepted page for re-selection. */
  kind: 'live' | 'preview' | 'amend'
  status: string
  quote: {
    quoteNumber: string; title: string; coverNote: string
    /** Decision 104: free-text footer shown after the contract value. */
    footerText: string
    validUntil: string | null; eccNotice: boolean; termsVersion: string | null; version: number
    /** Decision 134: the cited terms DOCUMENT, frozen at send — null = citation only. */
    termsUrl: string | null
  }
  issuer: {
    name: string; logoUrl: string | null; brand: string; trading: string
    web: string; email: string; phone: string
    legal: string; companyNo: string; regOffice: string; vat: string; isReseller: boolean
  }
  customer: { name: string; urn: string | null; postcode: string | null } | null
  recipient: { name: string | null; email: string } | null
  preparedBy: { name: string | null; email: string } | null
  sites: Array<{ id: string; name: string; urn: string | null; postcode: string | null; contact: string | null }>
  groups: Array<{ id: string; name: string; sort: number; kind: 'section' | 'choose_one'; siteId: string | null; parentGroupId: string | null; optional: boolean; preselected: boolean; choiceSet: string | null; subtotalOnly: boolean }>
  lines: PublicLine[]
  selections: SelectionMap
  totals: { oneOffSell: number; annualSell: number; contractValueSell: number; termMonths: number; lineCount: number; hasSurveyLines: boolean }
  attachments: Array<{ assetId: string; version: number; title: string; filename: string; category: string | null }>
  caveat: { text: string; version: string }
  nextSteps: string
  acceptance: {
    selections: Array<{ itemId: string; qty: number }>
    accepterName: string | null; poReference: string | null; poToFollow: boolean
    authorityNote: string | null
    termsVersion: string | null; acceptedAt: string
    /** Decision 218: 1 until amended; previous = the acceptance this version replaced. */
    version: number; amendedAt: string | null; amendedByName: string | null
    previous: { version: number; acceptedAt: string; amendedAt: string } | null
  } | null
  /** Decision 218: this link may amend the acceptance. */
  amendable: boolean
  successorAvailable: boolean
}

async function api<T>(path: string, method: 'GET' | 'POST' = 'GET', body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({})) as Record<string, unknown>
  if (!res.ok) throw new Error((data.error as string) ?? `Request failed (${res.status})`)
  return data as T
}

export function CustomerQuote({ token }: { token: string }) {
  const [payload, setPayload] = useState<Payload | null>(null)
  const [missing, setMissing] = useState(false)
  const [selections, setSelections] = useState<SelectionMap>({})
  const [modal, setModal] = useState<null | 'accept' | 'decline' | 'question'>(null)
  const [banner, setBanner] = useState<string | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout>>()

  const isPrint = useMemo(() => new URLSearchParams(window.location.search).get('print') === '1', [])

  useEffect(() => {
    // Decision 144: the print render tells the API what it is — the PDF
    // function's server-side page load must never count as a customer view.
    api<Payload>(`/api/q/${token}${isPrint ? '?print=1' : ''}`)
      .then((p) => { setPayload(p); setSelections(p.selections ?? {}) })
      .catch(() => setMissing(true))
  }, [token, isPrint])

  // Decision 70: the PDF renderer waits on this flag before printing.
  useEffect(() => {
    if (payload || missing) window.__quoteReady = true
  }, [payload, missing])

  const live = payload?.kind === 'live' && (payload.status === 'sent' || payload.status === 'viewed')
  // Decision 218: an amend link on an accepted quote works exactly like a live
  // page — switches move, working state persists — until the customer confirms.
  const amending = payload?.kind === 'amend' && payload.status === 'accepted' && payload.amendable === true
  const interactive = (live || amending) && !isPrint

  // Persist working selections server-side, debounced (decision 71). Preview
  // tokens keep selections purely local — non-binding by construction (75).
  const persist = useCallback((next: SelectionMap) => {
    if (!live && !amending) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      api(`/api/q/${token}/selection`, 'POST', { selections: next }).catch(() => {})
    }, 600)
  }, [live, amending, token])

  const updateSelections = (next: SelectionMap) => {
    setSelections(next)
    persist(next)
  }

  if (missing) return <NotFound />
  if (!payload) return <div className="qpage"><p className="muted" style={{ textAlign: 'center', padding: 60 }}>Loading your quotation…</p></div>

  const { issuer, quote, groups, lines, sites } = payload
  const brand = issuer.brand
  const multi = sites.length > 1
  const siteById = new Map(sites.map((s) => [s.id, s]))
  // Decision 218: an amend link re-opens the decided page — sections move again.
  const decided = !amending && (payload.status === 'accepted' || payload.status === 'declined'
    || payload.status === 'expired' || payload.status === 'superseded'
    || payload.status === 'withdrawn')

  // Accepted quotes render the FROZEN record (decision 74): only the accepted
  // lines, at the accepted quantities, read-only forever.
  // ⚠️ Totals-audit fix (2026-08-08): the reconstruction must carry SECTION
  // states too (`s:` keys — a section was on iff any of its lines was
  // accepted), or optional/choice-set sections re-resolve from their flags and
  // the totals block disagrees with the frozen record the rows show.
  const effectiveSelections: SelectionMap = payload.status === 'accepted' && payload.acceptance && !amending
    ? (() => {
        const acceptedIds = new Set(payload.acceptance.selections.map((s) => s.itemId))
        const sectionOnFrozen = (gId: string) => lines.some((l) =>
          acceptedIds.has(l.id) && (l.groupId === gId || groups.find((c) => c.id === l.groupId)?.parentGroupId === gId))
        return Object.fromEntries([
          ...lines.map((l) => [l.id, { selected: false }]),
          ...groups.filter((g) => g.kind === 'section').map((g) => [`s:${g.id}`, { selected: sectionOnFrozen(g.id) }]),
          ...payload.acceptance!.selections.map((s) => [s.itemId, { selected: true, qty: s.qty }]),
        ])
      })()
    : selections

  const selectionGroups = groups.map((g) => ({
    id: g.id, kind: g.kind, parentGroupId: g.parentGroupId,
    optional: g.optional, preselected: g.preselected,
    choiceSet: g.choiceSet ?? null, sort: g.sort,
  }))
  // Decision 117: one shared answer to "which sections count" — standalone,
  // optional AND choice-set members all resolve here.
  const includedIds = includedSectionIds(selectionGroups, effectiveSelections)
  const totals = publicTotals(computeTotals(lines.map(publicLineToQuoteLine), effectiveSelections, selectionGroups))
  // Decision 119: per-section subtotals for subtotal-priced sections — live,
  // so an option toggle or qty change moves the section figure with it.
  const subTotals = sectionSubtotals(lines.map(publicLineToQuoteLine), effectiveSelections, selectionGroups)

  const isPicked = (line: PublicLine, groupLines: PublicLine[]) => {
    if (line.optionality === 'required') return payload.status !== 'accepted' || amending || effectiveSelections[line.id]?.selected !== false
    if (line.optionality === 'optional') return effectiveSelections[line.id]?.selected ?? line.preselected
    // Decision 107: no preselected member = optional group — none is valid.
    const pickedId = groupLines.find((l) => effectiveSelections[l.id]?.selected)?.id
      ?? groupLines.find((l) => l.preselected)?.id
    return pickedId === line.id
  }

  const setSel = (lineId: string, patch: { selected?: boolean; qty?: number }) => {
    updateSelections({ ...effectiveSelections, [lineId]: { ...effectiveSelections[lineId], ...patch } })
  }

  const renderRow = (l: PublicLine, blockLines: PublicLine[], optionLabel?: string, sectionOn = true, subOnly = false) => {
    const isChoice = l.optionality === 'option'
    // Decision 116: a line in an EXCLUDED section is not on the quotation —
    // no tick, no total, whatever its own optionality says.
    const picked = sectionOn && isPicked(l, blockLines)
    const qty = effectiveSelections[l.id]?.qty ?? l.qty
    // Decision 253: a term shorter than a year is shown over its term, not per year.
    const subYear = l.recurring && l.termMonths != null && l.termMonths > 0 && l.termMonths < 12
    const perYear = annualised(l.unitPrice, l)
    const unit = l.recurring ? (subYear ? Math.round(perYear * l.termMonths! / 12 * 100) / 100 : perYear) : l.unitPrice
    const perLabel = l.recurring ? (subYear ? `over ${termLabel(l.termMonths)}` : 'per year') : 'one-off'
    const selectable = (isChoice || l.optionality === 'optional') && interactive && sectionOn
    return (
      <div key={l.id} className={`pline ${subOnly ? 'pline--sub' : ''} ${picked ? '' : 'pline--off'}`}>
        <span className="pline__ctl">
          {!sectionOn || l.optionality === 'required' || !interactive
            ? (picked ? <span className="ptick" title="Included">✓</span> : <span className="muted">—</span>)
            : selectable && (
              <label className={`switch ${picked ? 'switch--on' : ''}`} title={picked ? 'Selected' : 'Not selected'}>
                <input
                  type="checkbox"
                  checked={picked}
                  onChange={(e) => {
                    if (isChoice) {
                      // Decision 107: a group with NO default is optional — the
                      // picked switch toggles OFF back to none. With a default,
                      // exactly one always stays picked.
                      const hasDefault = blockLines.some((x) => x.preselected)
                      const turnOff = picked && !hasDefault && !e.target.checked
                      const next = { ...effectiveSelections }
                      for (const x of blockLines) {
                        next[x.id] = { ...next[x.id], selected: turnOff ? false : x.id === l.id }
                      }
                      updateSelections(next)
                    } else {
                      setSel(l.id, { selected: e.target.checked })
                    }
                  }}
                />
                <span className="switch__track"><span className="switch__knob" /></span>
              </label>
            )}
        </span>
        <div className="pline__main">
          <div style={{ fontWeight: 550, fontSize: 12.5 }}>
            {optionLabel && <span className="pill pill--info" style={{ marginRight: 6 }}>{optionLabel}</span>}
            {l.name}{' '}
            {l.subjectToSurvey && <span className="pill pill--warn">price may vary following survey</span>}
          </div>
          {l.description && <div className="pline__desc">{l.description}</div>}
        </div>
        <div className="pline__qty small">
          {l.qtyEditable && picked && interactive
            ? <>
                <span className="qty">
                  <button onClick={() => setSel(l.id, { qty: round2(Math.max(l.qtyMin ?? 1, qty - 1)) })}>−</button>
                  <input readOnly value={qty} />
                  <button onClick={() => setSel(l.id, { qty: round2(Math.min(l.qtyMax ?? QTY_MAX, qty + 1)) })}>+</button>
                </span>
                <div className="tiny muted">({l.qtyMin}–{l.qtyMax})</div>
              </>
            : <span className="muted">× {qty}</span>}
        </div>
        {/* Decision 119: subtotal-priced sections carry NO per-line prices —
            the section subtotal row is where the money shows. */}
        {!subOnly && (
          <div className="pline__unit num">
            {formatGBP(unit)}
            <div className="tiny muted">{l.recurring ? (subYear ? `each ${perLabel}` : 'each /yr') : 'each'}</div>
          </div>
        )}
        {!subOnly && (
          <div className="pline__total num">
            {picked ? formatGBP(unit * qty) : <span className="muted">—</span>}
            {picked && <div className="tiny muted">{perLabel}</div>}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`qpage ${isPrint ? 'qpage--print' : ''}`}>
      {payload.kind === 'preview' && <div className="qwatermark" aria-hidden>PREVIEW</div>}

      {!isPrint && <StatusBanner payload={payload} token={token} />}
      {banner && !isPrint && <div className="qbanner qbanner--ok">{banner}</div>}
      {/* Decision 140: phones get a gentle steer — the page now reads on a
          small screen, but the note offers the PDF and says where it shines. */}
      {!isPrint && <MobileNote token={token} quoteNumber={quote.quoteNumber} hasPack={payload.attachments.length > 0} />}

      <div className="pdoc" style={{ ['--brand' as never]: brand }}>
        <div className="pdoc__brand">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start', maxWidth: 320 }}>
            {issuer.logoUrl
              ? <img src={issuer.logoUrl} alt={issuer.name} style={{ height: 44, background: '#fff', borderRadius: 9, padding: issuer.isReseller ? 0 : '4px 8px' }} />
              : <div className="pdoc__logo">{(issuer.name || 'Q')[0]}</div>}
            <div>
              <strong style={{ fontSize: 15 }}>{issuer.name}</strong>
              <div className="small muted" style={{ marginTop: 3, lineHeight: 1.5 }}>
                {issuer.trading}<br />
                {issuer.web}<br />
                {issuer.phone}
                {payload.preparedBy && (
                  <><br /><span style={{ color: 'var(--ink, #1c2b33)' }}>
                    Prepared by {payload.preparedBy.name ?? payload.preparedBy.email}
                    {payload.preparedBy.name ? <span className="muted"> · {payload.preparedBy.email}</span> : null}
                  </span></>
                )}
              </div>
            </div>
          </div>
          {/* Decision 140: inline align/width moved to classes so the phone
              breakpoint can restack the header. */}
          <div className="pdoc__meta">
            <div style={{ fontSize: 15, fontWeight: 700, color: brand, letterSpacing: '0.04em' }}>QUOTATION</div>
            <div className="small muted" style={{ margin: '3px 0 8px' }}>
              {quote.quoteNumber}{quote.version > 1 ? ` · rev ${quote.version}` : ''}
              {quote.validUntil ? ` · valid until ${formatUKDate(quote.validUntil)}` : ''}
            </div>
            <div className="pdoc__prep">
              <div className="tiny muted" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Prepared for</div>
              <strong>{payload.customer?.name ?? '—'}</strong>
              {payload.recipient
                ? <div className="muted">Attn: {payload.recipient.name || payload.recipient.email} · {payload.recipient.email}</div>
                : !multi && sites[0]?.contact
                  ? <div className="muted">Attn: {sites[0].contact}</div>
                  : null}
              {!multi && sites[0] && (
                <div className="muted">{sites[0].postcode ?? ''}{sites[0].urn ? ` · URN ${sites[0].urn}` : ''}</div>
              )}
              {multi && (
                <div className="muted" style={{ marginTop: 3 }}>
                  Sites covered:<br />
                  {sites.map((s) => <span key={s.id}>• {s.name} ({s.postcode ?? '—'}{s.urn ? `, URN ${s.urn}` : ''})<br /></span>)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="pdoc__body">
          {quote.title && <div className="ptitle">{quote.title}</div>}
          {quote.coverNote && <div className="covernote"><QuoteMarkup text={quote.coverNote} /></div>}

          {(() => {
          const topLevel = groups.filter((g) => !g.parentGroupId)
          // Decision 117: pick a set member -> its siblings switch off; picking
          // the chosen one again in a DEFAULTLESS set returns the set to none.
          const pickSetMember = (g: typeof topLevel[number], currentlyOn: boolean) => {
            const members = topLevel.filter((x) => x.choiceSet === g.choiceSet)
            const noDefault = members.every((m) => !m.preselected)
            const next = { ...effectiveSelections }
            for (const m of members) {
              next[`s:${m.id}`] = { ...next[`s:${m.id}`], selected: currentlyOn && noDefault ? false : m.id === g.id }
            }
            updateSelections(next)
          }
          const renderSection = (g: typeof topLevel[number], optionIndex?: number) => {
            const own = lines.filter((l) => l.groupId === g.id)
            const blocks = groups
              .filter((c) => c.parentGroupId === g.id)
              .map((c) => ({ block: c, blockLines: lines.filter((l) => l.groupId === c.id) }))
              .filter((b) => b.blockLines.length > 0)
            if (own.length === 0 && blocks.length === 0) return null
            const site = g.siteId ? siteById.get(g.siteId) : null
            // Decision 87: an optional section is a customer switch of its own.
            // Accepted quotes read the FROZEN record: the section was included
            // iff any of its lines were accepted.
            const sectionLineIds = [...own, ...blocks.flatMap((b) => b.blockLines)].map((l) => l.id)
            const sectionOn = (g.optional || g.choiceSet)
              ? (payload.status === 'accepted' && payload.acceptance && !amending
                    ? sectionLineIds.some((id) => effectiveSelections[id]?.selected === true)
                    : includedIds.has(g.id))
              : true
            // Decision 119: subtotal-priced section — per-line prices hidden,
            // the money shows as one-off + per-year subtotals at the foot.
            const subOnly = g.subtotalOnly
            const st = subTotals.get(g.id) ?? { oneOffSell: 0, annualSell: 0 }
            return (
              <div key={g.id} className={`psec ${subOnly ? 'psec--sub' : ''}`} style={sectionOn ? undefined : { opacity: 0.55 }}>
                <h2>
                  {optionIndex != null && <span className="pill pill--info" style={{ marginRight: 8, verticalAlign: 'middle' }}>Alternative {String.fromCharCode(65 + optionIndex)}</span>}
                  {g.name}{multi && site ? <> — <span style={{ fontWeight: 400 }}>{site.name} ({site.postcode ?? '—'})</span></> : null}
                  {g.optional && !g.choiceSet && (
                    <label className={`switch ${sectionOn ? 'switch--on' : ''}`} style={{ marginLeft: 10, verticalAlign: 'middle' }}
                      title={sectionOn ? 'Section included — toggle to remove it from this quotation' : 'Section not included — toggle to add it'}>
                      <input type="checkbox" checked={sectionOn} disabled={decided}
                        onChange={(e) => setSel(`s:${g.id}`, { selected: e.target.checked })} />
                      <span className="switch__track"><span className="switch__knob" /></span>
                    </label>
                  )}
                  {g.choiceSet && (
                    <label className={`switch ${sectionOn ? 'switch--on' : ''}`} style={{ marginLeft: 10, verticalAlign: 'middle' }}
                      title={sectionOn ? 'This alternative is selected' : 'Choose this alternative'}>
                      <input type="checkbox" checked={sectionOn} disabled={decided}
                        onChange={() => pickSetMember(g, sectionOn)} />
                      <span className="switch__track"><span className="switch__knob" /></span>
                    </label>
                  )}
                  {g.optional && !g.choiceSet && <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 400 }}>{sectionOn ? 'optional — included' : 'optional — not included'}</span>}
                  {g.choiceSet && <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 400 }}>{sectionOn ? 'selected alternative' : 'not selected'}</span>}
                </h2>
                {!sectionOn && !decided && <div className="tiny muted" style={{ margin: '2px 0 6px' }}>Not included — use the switch above to add this section to the quotation.</div>}
                <div className="psec__head">
                  <span>Opt</span><span>Item</span><span>Qty</span>
                  {!subOnly && <span style={{ textAlign: 'right' }}>Unit Price</span>}
                  {!subOnly && <span style={{ textAlign: 'right' }}>Total Price</span>}
                </div>
                <div style={sectionOn ? undefined : { pointerEvents: 'none' }}>
                {g.kind === 'choose_one'
                  ? own.map((l, i) => (
                      <div key={l.id}>
                        {i > 0 && <div className="por">— OR —</div>}
                        {renderRow(l, own, `Option ${i + 1}`, sectionOn, subOnly)}
                      </div>
                    ))
                  : <>
                      {own.map((l) => renderRow(l, own, undefined, sectionOn, subOnly))}
                      {blocks.map(({ block, blockLines }) => (
                        <div key={block.id} className="pblock">
                          <div className="pblock__intro">{block.name} — choose <strong>ONE</strong> of the following{blockLines.every((x) => !x.preselected) ? ' (optional — or none)' : ''}:</div>
                          {blockLines.map((l, i) => (
                            <div key={l.id}>
                              {i > 0 && <div className="por">— OR —</div>}
                              {renderRow(l, blockLines, `Option ${i + 1}`, sectionOn, subOnly)}
                            </div>
                          ))}
                        </div>
                      ))}
                    </>}
                </div>
                {subOnly && (
                  <div className="psubtotal">
                    <span>Section subtotal (ex VAT)</span>
                    {sectionOn
                      ? <span className="num">
                          {st.oneOffSell > 0 && <>One-off {formatGBP(st.oneOffSell)}</>}
                          {st.oneOffSell > 0 && st.annualSell > 0 && <span className="psubtotal__sep"> · </span>}
                          {st.annualSell > 0 && <>{formatGBP(st.annualSell)} per year</>}
                          {(st.oneOffSell > 0 || st.annualSell > 0) && (st.termSell ?? 0) > 0 && <span className="psubtotal__sep"> · </span>}
                          {(st.termSell ?? 0) > 0 && <>{formatGBP(st.termSell!)} over term</>}
                          {st.oneOffSell === 0 && st.annualSell === 0 && !(st.termSell ?? 0) && formatGBP(0)}
                        </span>
                      : <span className="muted">—</span>}
                  </div>
                )}
              </div>
            )
          }
          const renderedSets = new Set<string>()
          return topLevel.map((g) => {
            if (!g.choiceSet) return renderSection(g)
            if (renderedSets.has(g.choiceSet)) return null
            renderedSets.add(g.choiceSet)
            const members = topLevel.filter((x) => x.choiceSet === g.choiceSet)
            if (members.length === 1) return renderSection(g)     // a set of one is just a section
            const noDefault = members.every((m) => !m.preselected)
            return (
              <div key={`set:${g.choiceSet}`} className="pchoiceset">
                <div className="pblock__intro">Choose <strong>ONE</strong> of the following alternatives{noDefault ? ' (optional — or none)' : ''}:</div>
                {members.map((m, i) => (
                  <div key={m.id}>
                    {i > 0 && <div className="por">— OR —</div>}
                    {renderSection(m, i)}
                  </div>
                ))}
              </div>
            )
          })
          })()}

          {quote.eccNotice && (
            <div className="pnote">⚠ <strong>Excess construction charges may apply following survey.</strong>{' '}
              If the site survey identifies additional construction work, any charges will be confirmed with you in a revised quotation before you are committed.</div>
          )}

          {payload.attachments.length > 0 && (
            <div className="qdocs">
              <h2>Supporting documents</h2>
              {payload.attachments.map((a) => (
                <a key={a.assetId} className="qdocs__row" href={`/api/q/${token}/asset/${a.assetId}`}>
                  <span className="qdocs__icon">📄</span>
                  <span style={{ flex: 1 }}>
                    <strong>{a.title}</strong>
                    {a.category && (
                      <span className="pill tiny" style={{ marginLeft: 8, ...pillStyleFor(a.category) }}>{a.category}</span>
                    )}
                  </span>
                  <span className="small muted">Download</span>
                </a>
              ))}
            </div>
          )}

          <div className="ptotalsrow">
            {/* Decision 115: the PDF carries a live door back to the page —
                a real link in the printed document, left of the totals. */}
            {isPrint && payload.kind !== 'preview' && (
              <a className="pviewlive" href={`${window.location.origin}/q/${token}`}>
                View &amp; accept this quotation online →
              </a>
            )}
            <div className="ptotals">
              <div><span className="muted">One-off charges</span><span className="num">{formatGBP(totals.oneOffSell)}</span></div>
              <div><span className="muted">Annual recurring</span><span className="num">{formatGBP(totals.annualSell)} /yr</span></div>
              <div className="grand"><span>Contract value (ex VAT)</span><span className="num">{formatGBPCompact(totals.contractValueSell)}</span></div>
            </div>
          </div>

          {/* Decision 104: free-text footer — right after the contract value,
              identical classes to the Builder preview (decision 69). */}
          {quote.footerText.trim() !== '' && <div className="pfooter"><QuoteMarkup text={quote.footerText} /></div>}

          {payload.status === 'accepted' && payload.acceptance && amending ? (
            <div className="paccept" data-testid="amend-block">
              <p style={{ margin: '0 0 6px', fontWeight: 650 }}>
                ✎ Amending your acceptance of {formatUKDate(payload.acceptance.acceptedAt)}
              </p>
              <p className="small muted" style={{ margin: '0 0 10px' }}>
                Change the options and quantities above to what you now need, then confirm. The amended selection replaces the one on record — the original acceptance date stands and prices are unchanged. Nothing changes until you confirm.
              </p>
              <button className="btn btn--primary" onClick={() => setModal('accept')}>Confirm amended acceptance</button>
            </div>
          ) : payload.status === 'accepted' && payload.acceptance ? (
            <div className="paccept">
              <p style={{ margin: '0 0 2px', fontWeight: 650, color: 'var(--ok, #1c8a4d)' }}>
                ✓ Accepted on {formatUKDate(payload.acceptance.acceptedAt)}
                {payload.acceptance.version > 1 && payload.acceptance.amendedAt
                  ? <span className="small muted" style={{ fontWeight: 400 }}> · amended {formatUKDate(payload.acceptance.amendedAt)}{payload.acceptance.amendedByName ? ` by ${payload.acceptance.amendedByName}` : ''}</span>
                  : null}
              </p>
              {payload.acceptance.previous && (
                <p className="tiny muted" style={{ margin: '0 0 6px' }} data-testid="previous-acceptance">
                  This amended selection replaces the one accepted on {formatUKDate(payload.acceptance.previous.acceptedAt)}{payload.acceptance.version > 2 ? ` (version ${payload.acceptance.version}; ${payload.acceptance.version - 1} earlier)` : ''}.
                </p>
              )}
              {payload.acceptance.accepterName && (
                <div className="qsig">
                  <span className="qsig__name">{payload.acceptance.accepterName}</span>
                  <span className="qsig__meta">Accepted and signed · {formatUKDate(payload.acceptance.acceptedAt)}</span>
                </div>
              )}
              <p className="small muted" style={{ margin: '0 0 10px' }}>
                {payload.acceptance.poReference
                  ? <>PO: {payload.acceptance.poReference}</>
                  : payload.acceptance.poToFollow ? <>PO to follow</> : <>No PO given</>}
                {payload.acceptance.authorityNote ? <> · authority to accept confirmed<span title={payload.acceptance.authorityNote}> *</span></> : null}
                {payload.acceptance.termsVersion
                  ? <> · Terms: {quote.termsUrl
                      ? <a href={quote.termsUrl} target="_blank" rel="noreferrer">{payload.acceptance.termsVersion}</a>
                      : payload.acceptance.termsVersion}</>
                  : null}
                {' '}· Acceptance is subject to survey / technical due diligence.
              </p>
              {payload.acceptance.authorityNote && (
                <p className="tiny muted" style={{ margin: '0 0 10px', fontStyle: 'italic' }}>* “{payload.acceptance.authorityNote}”</p>
              )}
              <p className="small" style={{ margin: 0, textAlign: 'left', whiteSpace: 'pre-wrap' }}>{payload.nextSteps}</p>
              {!isPrint && (
                <p style={{ marginTop: 12 }}>
                  <PdfDownloads token={token} quoteNumber={quote.quoteNumber} hasPack={payload.attachments.length > 0} soloLabel="Download PDF for your records" />
                </p>
              )}
            </div>
          ) : (
            <div className="paccept">
              <p className="small muted" style={{ margin: '0 0 10px' }}>
                This quotation is subject to{' '}
                {/* Decision 134: the citation is a DOOR when the document exists —
                    the uploaded PDF or the hosted URL, frozen at send. */}
                {quote.termsUrl
                  ? <a href={quote.termsUrl} target="_blank" rel="noreferrer" title="Open the terms this quotation is subject to">
                      <strong>{quote.termsVersion ?? (issuer.isReseller ? `${issuer.name}'s terms of business` : 'the issuer’s terms of business')}</strong>
                    </a>
                  : <strong>{quote.termsVersion ?? (issuer.isReseller ? `${issuer.name}'s terms of business` : 'the issuer’s terms of business')}</strong>}
                {' '}— the version you accept is recorded. {payload.caveat.text}
              </p>
              {interactive && (
                <>
                  <button className="btn btn--primary" onClick={() => setModal('accept')}>Accept this quotation</button>{' '}
                  <PdfDownloads token={token} quoteNumber={quote.quoteNumber} hasPack={payload.attachments.length > 0} />{' '}
                  {/* decision 89: ask-a-question retired — questions arrive as
                      replies to the owner's email and land on the HubSpot timeline */}
                  {/* Decision 140: decline reads as what it is — red, white text. */}
                  <button className="btn btn--sm btn--danger" onClick={() => setModal('decline')}>Decline</button>
                </>
              )}
              {payload.kind === 'preview' && !isPrint && (
                <p className="tiny muted" style={{ margin: 0 }}>This is a preview — it cannot be accepted, and selections are not saved.</p>
              )}
              {decided && !isPrint && payload.status !== 'accepted' && (
                <PdfDownloads token={token} quoteNumber={quote.quoteNumber} hasPack={payload.attachments.length > 0} />
              )}
            </div>
          )}
        </div>

        <div className="pfoot">
          {issuer.legal} · Registered in England &amp; Wales No. {issuer.companyNo} · Registered office: {issuer.regOffice} · VAT No. {issuer.vat}<br />
          All prices exclude VAT. {!issuer.isReseller && 'School data © GIAS, Open Government Licence v3.0. '}This quotation is issued subject to the terms referenced above.
        </div>
      </div>

      {modal === 'accept' && (
        <AcceptModal
          token={token}
          caveat={payload.caveat.text}
          termsVersion={quote.termsVersion}
          contractValue={totals.contractValueSell}
          organisationName={payload.customer?.name ?? null}
          defaultName={payload.recipient?.name ?? ''}
          defaultEmail={payload.recipient?.email ?? ''}
          selections={effectiveSelections}
          amend={amending ? { version: payload.acceptance?.version ?? 1 } : null}
          onDone={(p, link) => {
            setPayload(p); setModal(null)
            // Decision 218: the amend link is spent — carry on at the fresh live link.
            if (link) window.history.replaceState(null, '', link)
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'decline' && (
        <DeclineModal token={token} onDone={() => { setModal(null); window.location.reload() }} onClose={() => setModal(null)} />
      )}
      {modal === 'question' && (
        <QuestionModal
          token={token}
          defaultName={payload.recipient?.name ?? ''}
          defaultEmail={payload.recipient?.email ?? ''}
          onDone={(msg) => { setModal(null); setBanner(msg) }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------- state banner

// ---------------------------------------------------- PDF downloads (140)

/**
 * Decision 140: PDF downloads with visible progress. The render is slow
 * (headless-chromium cold start) — a bare <a> gives no feedback at all, so
 * these fetch the PDF with a busy spinner on the button, then hand the bytes
 * to the browser as a named download. Failure surfaces as a message instead
 * of a silent dead click.
 */
function usePdfDownload(token: string, quoteNumber: string, hasPack: boolean) {
  const [busy, setBusy] = useState<null | 'quote' | 'pack'>(null)
  const [error, setError] = useState<string | null>(null)

  const download = async (which: 'quote' | 'pack') => {
    if (busy) return
    setBusy(which); setError(null)
    try {
      const res = await fetch(`/api/q/${token}/pdf${which === 'quote' ? '?docs=0' : ''}`)
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(data.error ?? 'The PDF could not be generated just now — please try again.')
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Two distinct filenames when both downloads exist side by side.
      a.download = `${quoteNumber}${which === 'quote' && hasPack ? '-quotation' : ''}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The download failed — please try again.')
    } finally {
      setBusy(null)
    }
  }

  return { busy, error, download }
}

/**
 * Decision 140: when the quote carries supporting documents the customer
 * chooses — the quotation alone (`?docs=0`) or the full pack. Without
 * documents there is nothing to choose, so one button downloads the pack
 * (which IS the quotation, plus any cover sheet).
 */
function PdfDownloads({ token, quoteNumber, hasPack, soloLabel }: {
  token: string; quoteNumber: string; hasPack: boolean; soloLabel?: string
}) {
  const dl = usePdfDownload(token, quoteNumber, hasPack)
  const label = (which: 'quote' | 'pack', idle: string) =>
    dl.busy === which ? <><span className="spin" aria-hidden /> Preparing your PDF…</> : <>{idle}</>

  return (
    <span className="qdl">
      {hasPack ? (
        <>
          <button className="btn btn--sm btn--primary" disabled={dl.busy !== null} onClick={() => dl.download('quote')}>
            {label('quote', 'Download quotation (PDF)')}
          </button>
          <button className="btn btn--sm btn--primary" disabled={dl.busy !== null} onClick={() => dl.download('pack')}>
            {label('pack', 'Download full pack')}
          </button>
        </>
      ) : (
        <button className="btn btn--sm btn--primary" disabled={dl.busy !== null} onClick={() => dl.download('pack')}>
          {label('pack', soloLabel ?? 'Download PDF')}
        </button>
      )}
      {dl.error && <span className="qdl__err">{dl.error}</span>}
    </span>
  )
}

/**
 * Decision 140: shown only below the 640px breakpoint (CSS, no UA sniffing) —
 * the page itself now restacks for small screens, and this note offers the
 * PDF copy and says where the quotation reads best. Dismissible.
 */
function MobileNote({ token, quoteNumber, hasPack }: { token: string; quoteNumber: string; hasPack: boolean }) {
  const [gone, setGone] = useState(false)
  const dl = usePdfDownload(token, quoteNumber, hasPack)
  if (gone) return null
  return (
    <div className="qbanner qmobile">
      <span style={{ flex: 1 }}>
        This quotation is best viewed on a desktop. You can also{' '}
        <button className="qlinkbtn" disabled={dl.busy !== null} onClick={() => dl.download('quote')}>
          {dl.busy ? 'preparing your PDF…' : 'download a PDF copy'}
        </button>.
        {dl.error && <span className="qdl__err" style={{ display: 'block' }}>{dl.error}</span>}
      </span>
      <button className="btn btn--sm btn--ghost" aria-label="Dismiss" onClick={() => setGone(true)}>✕</button>
    </div>
  )
}

function StatusBanner({ payload, token }: { payload: Payload; token: string }) {
  const [working, setWorking] = useState(false)
  if (payload.kind === 'preview') {
    return <div className="qbanner">This is a <strong>preview</strong> of the quotation — non-binding, for review only.</div>
  }
  // Decision 218: the amend link's banner — what this page is for, and that
  // nothing moves until they confirm. A spent or blocked link says so.
  if (payload.kind === 'amend' && payload.status === 'accepted') {
    return payload.amendable
      ? <div className="qbanner"><span>You are <strong>amending an accepted quotation</strong>. Change the options or quantities below and confirm — nothing changes until you do. This link works once.</span></div>
      : <div className="qbanner"><span>This acceptance can no longer be amended online — please contact the issuer.</span></div>
  }
  switch (payload.status) {
    case 'declined':
      return <div className="qbanner">This quotation was <strong>declined</strong>. If circumstances change, contact the issuer for a revised quotation.</div>
    case 'expired':
      return <div className="qbanner">This quotation has <strong>expired</strong>. Contact the issuer for a revised quotation — you can still ask a question below.</div>
    case 'withdrawn':
      return <div className="qbanner">This quotation has been <strong>withdrawn</strong> by the issuer. If you were expecting it, please contact them — a revised quotation may follow.</div>
    case 'superseded':
      return (
        <div className="qbanner">
          This quotation has been <strong>superseded</strong> by a revised version.
          {payload.successorAvailable && (
            <>{' '}
              <button className="btn btn--sm btn--primary" disabled={working} onClick={async () => {
                setWorking(true)
                try {
                  const r = await api<{ url: string }>(`/api/q/${token}/successor`, 'POST')
                  window.location.href = r.url
                } catch { setWorking(false) }
              }}>View the current quotation</button>
            </>
          )}
        </div>
      )
    default:
      return null
  }
}

// ---------------------------------------------------------------- modals

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="qmodal" role="dialog" aria-label={title}>
        <div className="qmodal__head">
          <h2>{title}</h2>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>
        <div className="qmodal__body">{children}</div>
      </div>
    </>
  )
}

function AcceptModal({ token, caveat, termsVersion, contractValue, organisationName, defaultName, defaultEmail, selections, amend = null, onDone, onClose }: {
  token: string; caveat: string; termsVersion: string | null; contractValue: number
  organisationName: string | null
  defaultName: string; defaultEmail: string
  selections: SelectionMap
  /** Decision 218: confirming an AMENDED selection on an accepted quotation. */
  amend?: { version: number } | null
  onDone: (p: Payload, link?: string | null) => void; onClose: () => void
}) {
  // Decision 83: default to the recipient the quote was SENT to — editable,
  // since the person accepting may not be the addressee.
  const [name, setName] = useState(defaultName)
  const [email, setEmail] = useState(defaultEmail)
  const [po, setPo] = useState('')
  const [poToFollow, setPoToFollow] = useState(false)
  const [authorityConfirmed, setAuthorityConfirmed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      const r = await api<{ payload: Payload; link?: string | null }>(`/api/q/${token}/accept`, 'POST', {
        name, email, poReference: po || undefined, poToFollow,
        authorityConfirmed,
        selections,
        ...(amend ? { expectedVersion: amend.version } : {}),
      })
      onDone(r.payload, r.link ?? null)
    } catch (e) {
      setError((e as Error).message); setBusy(false)
    }
  }

  return (
    <Modal title={amend ? 'Confirm your amended acceptance' : 'Accept this quotation'} onClose={onClose}>
      <p className="small">{amend
        ? <>You are confirming the currently selected options and quantities as your <strong>amended</strong> acceptance, at a contract value of <strong>{formatGBPCompact(contractValue)}</strong> ex VAT. It replaces the selection on record; the original acceptance date stands.</>
        : <>You are accepting the currently selected options and quantities, at a contract value of <strong>{formatGBPCompact(contractValue)}</strong> ex VAT.</>}</p>
      <div className="field"><label>Your name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" /></div>
      <div className="field"><label>Your email</label>
        <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.sch.uk" /></div>
      <div className="field"><label>{amend ? 'New purchase order (optional — blank keeps the one on record)' : 'Purchase order (optional)'}</label>
        <input className="input" value={po} disabled={poToFollow} onChange={(e) => setPo(e.target.value)} placeholder="PO reference" />
        <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
          <input type="checkbox" checked={poToFollow} onChange={(e) => { setPoToFollow(e.target.checked); if (e.target.checked) setPo('') }} />
          PO to follow
        </label></div>
      <div className="field" style={{ background: '#f6fafc', border: '1px solid var(--line)', borderRadius: 8, padding: '10px 12px' }}>
        <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input type="checkbox" checked={authorityConfirmed} onChange={(e) => setAuthorityConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
          <span>{amend ? amendAuthorityStatement(organisationName) : authorityStatement(organisationName)} <span className="tiny muted">(This statement is recorded with your acceptance.)</span></span>
        </label>
      </div>
      <p className="tiny muted">{caveat}{termsVersion ? ` This acceptance is made under ${termsVersion}, which is recorded with it.` : ''} Typing your name records it as your acceptance signature.</p>
      {error && <p className="pill pill--stop">{error}</p>}
      <button className="btn btn--primary" disabled={busy || !name.trim() || !email.includes('@') || !authorityConfirmed} onClick={submit}>
        {busy ? (amend ? 'Recording amendment…' : 'Recording acceptance…') : amend ? 'Confirm amended acceptance' : 'Accept quotation'}
      </button>
    </Modal>
  )
}

function DeclineModal({ token, onDone, onClose }: { token: string; onDone: () => void; onClose: () => void }) {
  const [reason, setReason] = useState<DeclineReasonKey | ''>('')
  const [detail, setDetail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      await api(`/api/q/${token}/decline`, 'POST', { reason, detail: detail || undefined })
      onDone()
    } catch (e) { setError((e as Error).message); setBusy(false) }
  }

  return (
    <Modal title="Decline this quotation" onClose={onClose}>
      <div className="field"><label>Reason</label>
        {DECLINE_REASONS.map((r) => (
          <label key={r.key} className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
            <input type="radio" name="decline-reason" checked={reason === r.key} onChange={() => setReason(r.key)} />
            {r.label}
          </label>
        ))}
      </div>
      <div className="field"><label>Anything you'd like to add? (optional)</label>
        <textarea className="input" rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} /></div>
      {error && <p className="pill pill--stop">{error}</p>}
      <button className="btn btn--danger" disabled={busy || !reason} onClick={submit}>
        {busy ? 'Sending…' : 'Decline quotation'}
      </button>
    </Modal>
  )
}

function QuestionModal({ token, defaultName, defaultEmail, onDone, onClose }: {
  token: string; defaultName: string; defaultEmail: string
  onDone: (msg: string) => void; onClose: () => void
}) {
  const [message, setMessage] = useState('')
  // Decision 83: default to the person the quote was SENT to — editable, since
  // someone else at the school may be the one asking.
  const [name, setName] = useState(defaultName)
  const [email, setEmail] = useState(defaultEmail)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      const r = await api<{ message: string }>(`/api/q/${token}/question`, 'POST', {
        message, name: name || undefined, email: email || undefined,
      })
      onDone(r.message)
    } catch (e) { setError((e as Error).message); setBusy(false) }
  }

  return (
    <Modal title="Ask a question" onClose={onClose}>
      <div className="field"><label>Your question</label>
        <textarea className="input" rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Can the installation happen during half term?" /></div>
      <div className="field"><label>Your name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="field"><label>Your email, so the reply reaches you</label>
        <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
      {error && <p className="pill pill--stop">{error}</p>}
      <button className="btn btn--primary" disabled={busy || !message.trim()} onClick={submit}>
        {busy ? 'Sending…' : 'Send question'}
      </button>
    </Modal>
  )
}

function NotFound() {
  return (
    <div className="qpage">
      <div className="pdoc" style={{ maxWidth: 560, padding: '40px 34px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 20, color: '#006699', margin: '0 0 10px' }}>Quotation not found</h1>
        <p className="muted" style={{ margin: 0 }}>
          This link isn't valid — it may have been revoked, or the address may not have copied completely.
          Please check the link in your email, or contact whoever sent you the quotation.
        </p>
      </div>
    </div>
  )
}
